import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/store/authStore";

export function useLanguagePreference() {
  const { i18n } = useTranslation();
  const { user } = useAuthStore();
  const [preference, setPreference] = useState<string>("auto");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch and apply user language preference on initial load
  useEffect(() => {
    const fetchLanguagePreference = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("user_settings")
          .select("language_preference")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        if (data) {
          setPreference(data.language_preference);

          // Apply the language preference
          if (data.language_preference === "auto") {
            // Use browser language, defaulting to 'en' if not supported
            const browserLang = navigator.language.substring(0, 2);
            const languageToUse = ["en", "de"].includes(browserLang)
              ? browserLang
              : "en";
            i18n.changeLanguage(languageToUse);
          } else {
            i18n.changeLanguage(data.language_preference);
          }
        }
      } catch (error) {
        console.error("Error fetching language preference:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguagePreference();
  }, [user?.id, i18n]);

  // Function to update language preference
  const updateLanguagePreference = async (newPreference: string) => {
    if (!user?.id) return false;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("user_settings")
        .update({ language_preference: newPreference })
        .eq("id", user.id);

      if (error) throw error;

      setPreference(newPreference);

      // Apply the language preference
      if (newPreference === "auto") {
        // Use browser language, defaulting to 'en' if not supported
        const browserLang = navigator.language.substring(0, 2);
        const languageToUse = ["en", "de"].includes(browserLang)
          ? browserLang
          : "en";
        i18n.changeLanguage(languageToUse);
      } else {
        i18n.changeLanguage(newPreference);
      }

      return true;
    } catch (error) {
      console.error("Error updating language preference:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { preference, isLoading, updateLanguagePreference };
}
